import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useOrganization } from '../../context/OrganizationContext';
import { useTranslation } from 'react-i18next';

/**
 * Meeting Type Configuration Component
 *
 * Allows users to configure what types of meetings their bot offers:
 * - Online (with video call link)
 * - In-person (physical location)
 * - Both (bot will ask user which they prefer)
 */
export default function MeetingTypeConfig({ playbook, botId, activePlaybookId, user }) {
  const { t } = useTranslation('bots');
  const { context } = useOrganization();
  const [saving, setSaving] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(
    playbook?.meetingConfig?.meetingTypes || ['online']
  );

  // Sync state when playbook data loads asynchronously
  useEffect(() => {
    if (playbook?.meetingConfig?.meetingTypes) {
      setSelectedTypes(playbook.meetingConfig.meetingTypes);
    }
  }, [playbook]);

  // Helper to get bots path (org/personal)
  const getBotsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'bots']
    : ['clientes', auth.currentUser?.uid || user?.uid, 'bots'];

  const meetingConfig = playbook?.meetingConfig || {
    meetingTypes: ['online'],
    defaultMeetingType: 'online',
    meetingPlatform: 'google'
  };

  const handleTypeToggle = (type) => {
    const current = selectedTypes;

    if (current.includes(type)) {
      // Remove type (but keep at least one)
      const updated = current.filter(t => t !== type);
      if (updated.length === 0) {
        alert(t('integrations.calendar.meeting.minTypeError'));
        return;
      }
      setSelectedTypes(updated);
    } else {
      // Add type
      setSelectedTypes([...current, type]);
    }
  };

  const handleSave = async () => {
    if (!user || !botId || !activePlaybookId) {
      alert(t('integrations.calendar.meeting.missingDataError'));
      return;
    }

    setSaving(true);
    try {
      // CORRECT LOCATION: Save to playbook subcollection
      const playbookRef = doc(db, ...getBotsPath(), botId, 'playbooks', activePlaybookId);

      // Verify playbook exists before updating
      const playbookSnap = await getDoc(playbookRef);
      if (!playbookSnap.exists()) {
        throw new Error(t('integrations.calendar.meeting.playbookNotFound'));
      }

      // Get calendar provider from the BOT's own integrations doc (calendar es
      // per-bot desde junio; el doc {owner}/integrations es el fósil y su
      // provider puede estar vacío o viejo).
      const integrationsRef = doc(db, ...getBotsPath(), botId, 'integrations', 'config');
      const integrationsSnap = await getDoc(integrationsRef);
      const calendarProvider = integrationsSnap.exists()
        ? integrationsSnap.data().calendar?.provider || 'google'
        : 'google';

      // Update playbook with new meetingConfig
      await updateDoc(playbookRef, {
        meetingConfig: {
          meetingTypes: selectedTypes,
          defaultMeetingType: selectedTypes[0],
          meetingPlatform: calendarProvider
        }
      });

      alert(t('integrations.calendar.meeting.savedSuccess'));
      console.log('[MeetingTypeConfig] ✅ Saved to:', playbookRef.path);
      console.log('[MeetingTypeConfig] ✅ meetingTypes:', selectedTypes);
    } catch (error) {
      console.error('Error saving meeting config:', error);
      alert(t('integrations.calendar.meeting.saveError', { message: error.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '1.5rem',
      marginTop: '1rem'
    }}>
      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
        {t('integrations.calendar.meeting.title')}
      </h3>
      <p style={{ color: '#718096', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
        {t('integrations.calendar.meeting.description')}
      </p>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e2e8f0'
      }}>
        {/* Online Option */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          padding: '1rem',
          border: `2px solid ${selectedTypes.includes('online') ? '#667eea' : '#e2e8f0'}`,
          background: selectedTypes.includes('online') ? '#f7fafc' : 'white',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}>
          <input
            type="checkbox"
            checked={selectedTypes.includes('online')}
            onChange={() => handleTypeToggle('online')}
            style={{
              marginTop: '0.25rem',
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.25rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>💻</span>
              <strong style={{ fontSize: '1rem' }}>{t('integrations.calendar.meeting.onlineLabel')}</strong>
            </div>
            <p style={{
              margin: 0,
              fontSize: '0.875rem',
              color: '#718096',
              lineHeight: '1.4'
            }}>
              {t('integrations.calendar.meeting.onlineDesc')}
            </p>
          </div>
        </label>

        {/* In-Person Option */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          padding: '1rem',
          border: `2px solid ${selectedTypes.includes('in-person') ? '#667eea' : '#e2e8f0'}`,
          background: selectedTypes.includes('in-person') ? '#f7fafc' : 'white',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}>
          <input
            type="checkbox"
            checked={selectedTypes.includes('in-person')}
            onChange={() => handleTypeToggle('in-person')}
            style={{
              marginTop: '0.25rem',
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.25rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📍</span>
              <strong style={{ fontSize: '1rem' }}>{t('integrations.calendar.meeting.inPersonLabel')}</strong>
            </div>
            <p style={{
              margin: 0,
              fontSize: '0.875rem',
              color: '#718096',
              lineHeight: '1.4'
            }}>
              {t('integrations.calendar.meeting.inPersonDesc')}
            </p>
          </div>
        </label>
      </div>

      {/* Info boxes based on selection */}
      <div style={{ marginTop: '1rem' }}>
        {selectedTypes.length === 2 && (
          <div style={{
            padding: '0.875rem',
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #2196f3',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#1565c0'
          }}>
            <strong>{t('integrations.calendar.meeting.modeAsk')}</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>
              {t('integrations.calendar.meeting.modeAskDesc')}
            </p>
          </div>
        )}

        {selectedTypes.length === 1 && selectedTypes[0] === 'online' && (
          <div style={{
            padding: '0.875rem',
            backgroundColor: '#e8f5e9',
            borderLeft: '4px solid #4caf50',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#2e7d32'
          }}>
            <strong>{t('integrations.calendar.meeting.modeOnline')}</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>
              {t('integrations.calendar.meeting.modeOnlineDesc')}
            </p>
          </div>
        )}

        {selectedTypes.length === 1 && selectedTypes[0] === 'in-person' && (
          <div style={{
            padding: '0.875rem',
            backgroundColor: '#fff3e0',
            borderLeft: '4px solid #ff9800',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#e65100'
          }}>
            <strong>{t('integrations.calendar.meeting.modeInPerson')}</strong>
            <p style={{ margin: '0.25rem 0 0 0' }}>
              {t('integrations.calendar.meeting.modeInPersonDesc')}
            </p>
          </div>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving || selectedTypes.length === 0}
        style={{
          width: '100%',
          padding: '0.75rem',
          background: saving || selectedTypes.length === 0 ? '#cbd5e0' : '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontWeight: 600,
          cursor: saving || selectedTypes.length === 0 ? 'not-allowed' : 'pointer',
          marginTop: '1rem',
          fontSize: '1rem'
        }}
      >
        {saving ? t('integrations.calendar.meeting.saving') : t('integrations.calendar.meeting.saveConfig')}
      </button>

      {/* Debug info (only in dev) */}
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#718096' }}>
          <summary style={{ cursor: 'pointer' }}>🔍 Debug Info</summary>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
            {JSON.stringify({
              botId,
              activePlaybookId,
              currentConfig: meetingConfig,
              selectedTypes
            }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
